import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useRoomContext } from '@livekit/components-react'
import { RoomEvent, type RemoteParticipant } from 'livekit-client'
import {
  clearHostViewStore,
  createHostViewSource,
  createHostViewStore,
  decodeHostView,
  encodeHostView,
  hostViewMoved,
  hostViewTopic,
  receiveHostView,
  type HostViewPose,
  type HostViewSource,
  type HostViewStore,
} from '@/lib/map/hostView'
import { mapHostOf } from './rules'
import type { StageFace } from './types'
import { useInRoom } from './useInRoom'

/** At most ten views a second while the map moves: smooth once eased, and a third of the cost of thirty. */
export const SEND_EVERY_MS = 100
/** How long the map holds still before its view is sent again reliably, as the one it settled on. */
export const SETTLE_MS = 300
/** How often the current view is sent reliably while the map is the call, so a viewer arriving mid-show follows soon. */
export const HEARTBEAT_MS = 5000

export interface HostMapViewInput {
  stageId: string
  /** The stage's call (`useWatchScreen`'s `calledIdentity`). */
  calledIdentity: string | null
  /** The stage's live members, as the room metadata has them. */
  members: readonly StageFace[]
  /** This tab's LiveKit identity. */
  localIdentity: string | null
}

export interface HostMapView {
  /** The live host whose map is the stage's call while their connection is in the room, this tab's own included; null otherwise. */
  host: StageFace | null
  /** This tab's map is the call: it sends its view. */
  sending: boolean
  /** Where a follower's map reads the host's view, kept outside React (ten a second would re-render the page). */
  store: HostViewStore
  /** The tracked athlete whose card the followed host has open: state, changing only when the card does. */
  athlete: string | null
  /** Where this tab's map (the world, or the Mapbox map) offers its view while it is the live map on screen. */
  source: HostViewSource
  /** This tab's map offers its view now: a live host may call their map. */
  shareable: boolean
}

/**
 * The host's map view on the stage's topic (docs/features/follow-host-map.md). While this tab's map is the call, it
 * sends the view its map offers (`source`): lossy at most ten times a second while it moves, reliably once it has
 * held still for `SETTLE_MS`, and again every `HEARTBEAT_MS` (the last view, even while the map is out of sight), so a
 * viewer who arrives follows within seconds. While another host's map is the call, it takes their views into `store`,
 * only from that host's connection (LiveKit names the sender; old viewer tokens can still publish data into the room),
 * newest first; the store empties when the call moves on or that connection leaves the room. The host's open athlete
 * card is the one part a page renders from, so it alone is state, set only when it changes. Use inside the room.
 */
export function useHostMapView({ stageId, calledIdentity, members, localIdentity }: HostMapViewInput): HostMapView {
  const room = useRoomContext()
  const called = mapHostOf(calledIdentity, members)
  const mine = called !== null && called.identity === localIdentity
  const present = useInRoom(called && !mine ? [called.identity] : [])
  const host = called && (mine || present) ? called : null
  const hostIdentity = host?.identity ?? null
  const sending = mine
  const [store] = useState(createHostViewStore)
  const [source] = useState(createHostViewSource)
  const shareable = useSyncExternalStore(source.subscribe, () => source.read !== null)
  const [athlete, setAthlete] = useState<string | null>(null)
  const athleteNow = useRef<string | null>(null)

  // A new host (or none) starts from nothing: an old view never shows for another call.
  useEffect(() => {
    clearHostViewStore(store)
    athleteNow.current = null
    setAthlete(null)
  }, [store, hostIdentity, stageId])

  useEffect(() => {
    if (!hostIdentity || sending) return
    const topic = hostViewTopic(stageId)
    const onData = (payload: Uint8Array, participant?: RemoteParticipant, _kind?: unknown, packetTopic?: string) => {
      if (packetTopic !== topic || participant?.identity !== hostIdentity) return
      const view = decodeHostView(payload)
      if (!view || !receiveHostView(store, view)) return
      const open = view.athlete ?? null
      if (open !== athleteNow.current) {
        athleteNow.current = open
        setAthlete(open)
      }
    }
    room.on(RoomEvent.DataReceived, onData)
    return () => {
      room.off(RoomEvent.DataReceived, onData)
    }
  }, [room, stageId, hostIdentity, sending, store])

  // Counts on across calls, so a follower never takes a new call's view for an old one.
  const seq = useRef(0)
  useEffect(() => {
    if (!sending) return
    const topic = hostViewTopic(stageId)
    let last: HostViewPose | null = null
    let changedAt = 0
    let settling = false
    let reliableAt = 0
    let warned = false
    const send = (pose: HostViewPose, reliable: boolean, now: number) => {
      seq.current += 1
      const payload = encodeHostView({ ...pose, seq: seq.current, at: now, basemap: source.basemap, settled: reliable })
      if (reliable) reliableAt = now
      const failed = (error: unknown) => {
        // A seat raised before hosts could publish data: their audience follows once they go live again.
        if (!warned) console.warn('The map’s view could not be sent', error)
        warned = true
      }
      try {
        void room.localParticipant.publishData(payload, { reliable, topic }).catch(failed)
      } catch (error) {
        failed(error)
      }
    }
    const tick = () => {
      const now = Date.now()
      const pose = source.read?.() ?? null
      if (pose && (!last || hostViewMoved(last, pose))) {
        // The first view goes reliably: it is what followers start from.
        send(pose, last === null, now)
        settling = last !== null
        last = pose
        changedAt = now
      } else if (last && settling && now - changedAt >= SETTLE_MS) {
        settling = false
        send(last, true, now)
      } else if (last && now - reliableAt >= HEARTBEAT_MS) {
        send(last, true, now)
      }
    }
    tick()
    const timer = window.setInterval(tick, SEND_EVERY_MS)
    return () => window.clearInterval(timer)
  }, [room, stageId, sending, source])

  return useMemo(() => ({ host, sending, store, athlete, source, shareable }), [host, sending, store, athlete, source, shareable])
}
